import { useCallback } from "react";
import { message } from "antd";
import html2pdf from "html2pdf.js";
import { renderReturnClassicTemplate } from "../templates";
import type { PageSizeType, TemplatesTypes } from "../templates";
import { TemplatesTypes as TT } from "../templates";

interface UseReturnInvoiceDocumentParams {
  orderReturn: any;
  config: any;
  pageSize: PageSizeType;
  template: TemplatesTypes;
  creditOnly?: boolean;
}

const formatInvoiceAmount = (value: unknown) => {
  const numericValue = Number.parseFloat(String(value));
  return Number.isNaN(numericValue) ? "0.00" : numericValue.toFixed(2);
};

const sanitizeFileName = (value: unknown) => {
  const name = String(value || "return")
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, "_")
    .trim();

  return name || "return";
};

const removeFrame = (frame: HTMLIFrameElement) => {
  if (frame.parentNode) {
    frame.parentNode.removeChild(frame);
  }
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to read PDF blob."));
    reader.readAsDataURL(blob);
  });

export const useReturnInvoiceDocument = ({
  orderReturn,
  config,
  pageSize,
  template,
  creditOnly = false,
}: UseReturnInvoiceDocumentParams) => {
  const documentLabel = creditOnly ? "Credit Note" : "Return Invoice";

  const getReturnHtml = useCallback(
    (isForPrint = false) => {
      if (!orderReturn) {
        return "";
      }

      return renderReturnClassicTemplate({
        orderReturn,
        config,
        pageSize,
        isForPrint,
        creditOnly,
      });
    },
    [config, orderReturn, pageSize, creditOnly],
  );

  const printInvoice = useCallback(() => {
    if (!orderReturn) {
      return;
    }

    const frame = document.createElement("iframe");
    frame.style.cssText = "position:absolute;top:-9999px;left:-9999px;";
    document.body.appendChild(frame);

    const doc = frame.contentWindow?.document || frame.contentDocument;
    if (!doc) {
      removeFrame(frame);
      return;
    }

    doc.open();
    doc.write(getReturnHtml(true));
    doc.close();

    window.setTimeout(() => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      window.setTimeout(() => removeFrame(frame), 1000);
    }, 500);
  }, [getReturnHtml, orderReturn]);

  const downloadInvoice = useCallback(() => {
    if (!orderReturn) {
      return;
    }

    const container = document.createElement("div");
    container.innerHTML = getReturnHtml(true);

    html2pdf()
      .from(container)
      .set({
        filename: `${documentLabel.replace(/\s/g, "")}_${sanitizeFileName(orderReturn.return_no)}.pdf`,
        margin: 10,
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
      })
      .save();

    message.success(`${documentLabel} PDF downloaded!`);
  }, [getReturnHtml, orderReturn, documentLabel]);

  const getReturnPdfBase64 = useCallback(async () => {
    if (!orderReturn) {
      return "";
    }

    const container = document.createElement("div");
    container.innerHTML = getReturnHtml(true);

    const blob = await html2pdf()
      .from(container)
      .set({
        filename: `${documentLabel.replace(/\s/g, "")}_${sanitizeFileName(orderReturn.return_no)}.pdf`,
        margin: 10,
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
      })
      .outputPdf("blob");

    return await blobToBase64(blob);
  }, [getReturnHtml, orderReturn, documentLabel]);

  return {
    getReturnHtml,
    printInvoice,
    downloadInvoice,
    getReturnPdfBase64,
  };
};